import {Button, Flag, FlagProps, useAnimatedPopper} from 'platform/components';
import {Box, Show, Text} from 'platform/foundation';
import styled, {css} from 'styled-components';

import {startTransition, useCallback, useEffect, useRef, useState} from 'react';

import i18n from '@dms/i18n';
import {testIds} from '@dms/routes';

import {convertStringToCamelCase, mergeRefs} from 'shared/web';

const VirtualWrapper = styled.div`
  width: 100%;
  position: relative;
`;

const TagsWrapper = styled.div<{$isAbsolute?: boolean}>`
  width: 100%;
  flex: 1 1 60%;
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  overflow: hidden;
  position: ${({$isAbsolute}) => ($isAbsolute ? 'absolute' : 'relative')};
`;

const HiddenTagsContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
`;

// eslint-disable-next-line eag/no-css-property -- Muted pre-existing violation
const FlagWrapper = styled.div<{isHidden?: boolean}>`
  margin-right: ${({theme}) => theme.getSize(1)};
  margin-bottom: ${({theme}) => theme.getSize(1)};

  span {
    word-break: break-word;
    white-space: initial;
  }
  ${({isHidden}) =>
    isHidden
      ? `
			height: 0px;
			padding-top: 0;
			padding-bottom: 0;
			overflow: hidden;
			margin-bottom: 0;
			margin-top: 0;
		`
      : ''};
`;

export type DynamicFlagListProps = {
  data: FlagProps[];
  onRecalculate?: (visibleCount: number) => void;
};

export const DynamicFlagList = ({data = [], onRecalculate}: DynamicFlagListProps) => {
  const [width, setWidth] = useState(typeof window === 'undefined' ? 0 : window.innerWidth);
  const [visibleCount, setVisibleCount] = useState<number>(0);

  const mounted = useRef(false);
  const divRef = useRef<HTMLDivElement>(null);
  const moreButtonRef = useRef<HTMLDivElement>(null);

  const realWrapperRef = useRef<HTMLDivElement>(null);

  const {openPopper, closePopper, referenceRef, Popper, popperProps} = useAnimatedPopper({
    placement: 'bottom',
    gutter: 4,
    usePortal: true,
    closeOnScroll: true,
  });

  useEffect(function observeWrapperWidth() {
    if (!realWrapperRef.current) {
      return;
    }

    // To reduce re-renders
    const handleResize = (width: number) => startTransition(() => setWidth(width));

    const ro = new ResizeObserver(([entry]) => {
      handleResize(entry.contentRect.width);
    });

    ro.observe(realWrapperRef.current);

    return () => {
      ro.disconnect();
    };
  }, []);

  useEffect(
    function notifyVisibleCountChange() {
      mounted.current && onRecalculate?.(visibleCount);

      mounted.current = true;
    },
    [visibleCount] // eslint-disable-line react/exhaustive-deps -- Muted pre-existing violation
  );

  const recalculateVisibleCount = useCallback(() => {
    // Additional pixels to prevent "more" button appearing in third row
    const errorCorrection = 10;

    const width = divRef.current?.getBoundingClientRect().width ?? 0;
    const moreButtonWidth = moreButtonRef.current?.offsetWidth ?? 0;
    const widthMinusButton = width - moreButtonWidth;
    const children = Array.from(divRef.current?.children ?? []);

    let count = 0;
    let usedWidth = 0;
    let isSecondRow = false;

    for (let i = 0; i < children.length; i++) {
      if (children[i] === moreButtonRef.current) {
        continue;
      }

      const style = getComputedStyle(children[i]);
      const elWidth = (children[i] as HTMLSpanElement).offsetWidth;
      const totalWidth =
        elWidth + parseInt(style.marginLeft || '0', 10) + parseInt(style.marginRight || '0', 10);

      if (usedWidth + totalWidth + errorCorrection >= (isSecondRow ? widthMinusButton : width)) {
        if (isSecondRow) {
          break;
        }

        i -= 1;
        usedWidth = 0;
        isSecondRow = true;
      } else {
        count += 1;
        usedWidth += totalWidth;
      }
    }

    setVisibleCount(count);
  }, []);

  useEffect(
    function recalculateOnResize() {
      recalculateVisibleCount();
    },
    [recalculateVisibleCount, width, data]
  );

  const hiddenTagsCount = data.length - visibleCount;

  return (
    <Box width="100%">
      {/* Virtual wrapper */}
      <VirtualWrapper>
        <TagsWrapper $isAbsolute ref={divRef}>
          {data.map(({label, ...props}, index) => (
            // eslint-disable-next-line react/no-array-index-key -- Pre-existing violation
            <FlagWrapper key={`${label}-${index}-virtual`} isHidden={true}>
              <Flag
                {...props}
                data-testid={testIds.vehicles.detail(
                  `virtual-highlight-${convertStringToCamelCase(label)}`
                )}
                label={label}
                size="small"
              />
            </FlagWrapper>
          ))}
        </TagsWrapper>
      </VirtualWrapper>
      {/* Real wrapper */}
      <TagsWrapper
        ref={mergeRefs(realWrapperRef, referenceRef)}
        data-testid={testIds.vehicles.detail('highlights-tags')}
      >
        {data.slice(0, visibleCount).map(({label, ...props}, index) => (
          <FlagWrapper
            key={`${label}-${index}-real`} // eslint-disable-line react/no-array-index-key -- Pre-existing violation
            isHidden={visibleCount === 0 ? false : index + 1 > visibleCount}
          >
            <Flag
              {...props}
              data-testid={testIds.vehicles.detail(
                `real-highlight-${convertStringToCamelCase(label)}`
              )}
              label={label}
              size="small"
            />
          </FlagWrapper>
        ))}

        <div
          ref={moreButtonRef}
          onMouseOver={openPopper}
          onFocus={openPopper}
          onMouseOut={closePopper}
          onBlur={closePopper}
          css={css`
            display: ${hiddenTagsCount > 0 ? 'block' : 'none'};
          `}
          data-testid={testIds.vehicles.detail('moreHighlights-wrapper')}
        >
          <Button
            variant="link"
            size="small"
            data-testid={testIds.vehicles.detail('moreHighlights-moreButton')}
            title={`+${hiddenTagsCount} ${i18n.t('general.labels.more')}`}
          />
        </div>

        <Show when={!data.length}>
          <Text
            color="tertiary"
            size="xSmall"
            data-testid={testIds.vehicles.detail('noHighlights')}
          >
            {i18n.t('entity.vehicle.labels.noHighlights')}
          </Text>
        </Show>
      </TagsWrapper>

      <Popper {...popperProps} data-testid={testIds.vehicles.detail('moreHighlights-dropdown')}>
        <Box
          padding={2}
          backgroundColor="general.white"
          borderRadius="medium"
          boxShadow="elevation_2"
        >
          {/* eslint-disable-next-line react/forbid-component-props -- Pre-existing violation */}
          <HiddenTagsContainer style={{width}}>
            {data.slice(visibleCount, data.length).map(({label, ...props}, index) => (
              // eslint-disable-next-line react/no-array-index-key -- Pre-existing violation
              <FlagWrapper key={`${label}-${index}-hidden`}>
                <Flag
                  {...props}
                  data-testid={testIds.vehicles.detail(`highlights-${index}`)}
                  label={label}
                  size="small"
                />
              </FlagWrapper>
            ))}
          </HiddenTagsContainer>
        </Box>
      </Popper>
    </Box>
  );
};
